import { useState } from 'react'

import { Box, Section, SelectOption } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import SelectField from 'modules/core/components/SelectField'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { CodeableType } from 'modules/payroll_integrations/lib/enums'
import codes from 'modules/payroll_integrations/store/collections/codes'
import type Code from 'modules/payroll_integrations/store/models/Code'

type CodeValue = '0' | '1'
type AllowedCodeableType = CodeableType.WORKPLACE_CODE_ENABLED

interface Props {
  integration_name: string
}

const CodesBasedOnSelectors = ({ integration_name }: Props) => {
  const [workplaceCodeValue, setWorkplaceCodeValue] = useState<CodeValue | null>(null)
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { loading } = useAsyncData(
    () =>
      codes
        .fetch({
          data: {
            integration: integration_name,
          },
        })
        .then(initializeValues),
    [integration_name]
  )

  const initializeValues = () => {
    const workplaceCodeValue = findCode(CodeableType.WORKPLACE_CODE_ENABLED)?.attributes.code

    if (workplaceCodeValue) setWorkplaceCodeValue(workplaceCodeValue as CodeValue)
  }

  const submit = (codeValue: CodeValue | null, codeableType: AllowedCodeableType) => {
    const code = findCode(codeableType)

    if (code && hasChanges(code, codeValue)) {
      update(code, codeValue!)
    } else if (codeValue != null) {
      create(codeableType, codeValue)
    }
  }

  const hasChanges = (oldCode: Code, newCodeValue: CodeValue | null) => {
    return newCodeValue != null && oldCode.attributes.code !== newCodeValue
  }

  const findCode = (type: AllowedCodeableType) => {
    return codes.find((code) => code.attributes.codeable_type === type)
  }

  const update = (code: Code, codeValue: CodeValue) => {
    code.update(
      {
        code: codeValue,
      },
      { patch: false }
    )
  }

  const create = async (type: CodeableType, codeValue: CodeValue) => {
    await codes.create({
      codeable_type: type,
      code: codeValue,
      codeable_id: companyId,
      integration: integration_name,
    })
  }

  const workplaceCodeSelector = () => {
    const options: SelectOption[] = [
      {
        label: i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.codes_based_on.workplace.workplace'
        ),
        value: 1,
      },
      {
        label: i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.codes_based_on.workplace.legal_entity'
        ),
        value: 0,
      },
    ]

    return (
      <SelectField
        label={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.codes_based_on.workplace.title'
        )}
        value={`${workplaceCodeValue}`}
        options={options}
        onChange={(workplaceCodeValue: string | null) => {
          const codeValue = workplaceCodeValue as CodeValue | null
          setWorkplaceCodeValue(codeValue)
          submit(codeValue, CodeableType.WORKPLACE_CODE_ENABLED)
        }}
      />
    )
  }

  if (loading) return null

  return (
    <Section
      description={i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.codes_based_on.description'
      )}
      illustration="window-user"
      title={i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.codes_based_on.title'
      )}
    >
      <Box
        background="grey200"
        borderRadius={{
          all: 'abs008',
        }}
        padding="s24"
        gap="s16"
      >
        <>{workplaceCodeSelector()}</>
      </Box>
    </Section>
  )
}

export default observer(CodesBasedOnSelectors)
